// GtAiCard — AI questions on the candidate record.
//
// Ask anything about this person ("Has led a team of five or more?") and get
// an answer tied to the facts we hold, with where each fact came from. Also
// shows every AI column answer from the benches they are on, and lets you ask
// a bench column for them on the spot. Internal only: never on the dossier,
// PDF or share link.
import { useState, useEffect, useCallback } from 'react';
import { GtSelect } from './GtControls';
import { useEnrichFetch } from './GtEnrich';
import { AiAnswerDetail } from './GtAiColumnRun';

const json = { 'Content-Type': 'application/json' };
const KINDS = [{ value: 'yesno', label: 'Yes, no or unknown' }, { value: 'short', label: 'Short answer' }];
function label(a) { return a === 'yes' ? 'Yes' : a === 'no' ? 'No' : a === 'unknown' ? 'Unknown' : a; }
function tone(a) { return ['yes', 'no', 'unknown'].indexOf(a) !== -1 ? a : 'text'; }

function Row({ title, sub, value, open, onToggle, actions }) {
  const v = value && typeof value === 'object' ? value : null;
  return (
    <div className={'gtaic-row' + (open ? ' gtaic-row--open' : '')}>
      <button type="button" className="gtaic-head" onClick={onToggle} aria-expanded={open}>
        <span className="gtaic-q">
          <b>{title}</b>
          {sub && <span>{sub}</span>}
        </span>
        {v ? <span className={'gtbn-ai gtbn-ai--' + tone(v.a)}><span className="gtbn-ai-a">{label(v.a)}</span></span>
          : <span className="gtbn-ai gtbn-ai--none"><span className="gtbn-ai-a">Ask</span></span>}
      </button>
      {open && (
        <div className="gtaic-body">
          {v ? <AiAnswerDetail value={v} /> : <p className="gten-muted">Not asked for this person yet.</p>}
          {actions && <div className="gtaic-acts">{actions}</div>}
        </div>
      )}
    </div>
  );
}

export default function GtAiCard({ candidateId, getToken }) {
  const authFetch = useEnrichFetch(getToken);
  const [data, setData] = useState(null);
  const [question, setQuestion] = useState('');
  const [kind, setKind] = useState('yesno');
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try { setData(await authFetch('/api/candidates/' + encodeURIComponent(candidateId) + '/ai-answers')); }
    catch (e) { setErr(e.message); setData({ own: [], bench: [] }); }
  }, [authFetch, candidateId]);
  useEffect(() => { load(); }, [load]);

  async function ask() {
    const q = question.trim();
    if (!q) return;
    setBusy('new'); setErr('');
    try {
      const r = await authFetch('/api/candidates/' + encodeURIComponent(candidateId) + '/ai-questions', {
        method: 'POST', headers: json, body: JSON.stringify({ question: q, kind }),
      });
      setQuestion('');
      await load();
      setOpen('o' + r.answer.id);
    } catch (e) { setErr(e.message); }
    setBusy(null);
  }
  async function askAgain(row) {
    setBusy('o' + row.id); setErr('');
    try {
      await authFetch('/api/candidates/' + encodeURIComponent(candidateId) + '/ai-questions/' + row.id + '/ask', { method: 'POST', headers: json, body: '{}' });
      await load();
    } catch (e) { setErr(e.message); }
    setBusy(null);
  }
  async function remove(row) {
    setBusy('o' + row.id); setErr('');
    try {
      await authFetch('/api/candidates/' + encodeURIComponent(candidateId) + '/ai-questions/' + row.id, { method: 'DELETE' });
      await load();
    } catch (e) { setErr(e.message); }
    setBusy(null);
  }
  async function askBench(b) {
    const id = 'b' + b.member_id + b.key;
    setBusy(id); setErr('');
    try {
      const r = await authFetch('/api/benches/' + b.bench_id + '/ai/run', { method: 'POST', headers: json, body: JSON.stringify({ key: b.key, member_ids: [b.member_id] }) });
      for (let i = 0; i < 40; i += 1) {
        const j = (await authFetch('/api/benches/' + b.bench_id + '/ai/jobs/' + r.job.id)).job;
        if (j.status === 'done') { if (j.error && !j.done) setErr(j.error); break; }
        await new Promise(z => setTimeout(z, 1000));
      }
      await load();
      setOpen(id);
    } catch (e) { setErr(e.message); }
    setBusy(null);
  }

  const own = (data && data.own) || [];
  const bench = (data && data.bench) || [];

  return (
    <div className="gtr-card gtaic-card">
      <div className="gtr-cardh">AI questions</div>
      <div className="gtaic-ask">
        <textarea value={question} maxLength={300} rows={2} aria-label="Ask a question about this person"
          placeholder="Ask about them, e.g. Has led a team of five or more?"
          onChange={e => setQuestion(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }} />
        <div className="gtaic-askrow">
          <div className="gtaic-kind"><GtSelect value={kind} onChange={setKind} options={KINDS} /></div>
          <button type="button" className="gtc-primary" disabled={!question.trim() || !!busy} onClick={ask}>{busy === 'new' ? 'Asking…' : 'Ask'}</button>
        </div>
        <p className="gten-muted gtaic-hint">Answered from what we hold, with the facts behind every answer. Never shown to clients.</p>
      </div>
      {err && <div className="gten-state gten-state--bad"><span>{err}</span></div>}
      {!data && <div className="gtr-empty-sm">Loading…</div>}

      {own.length > 0 && (
        <div className="gtaic-group">
          <div className="gtaic-group-h">Asked here</div>
          {own.map(r => (
            <Row key={r.id} title={r.question} sub={r.asked_by_name ? 'Asked by ' + r.asked_by_name : null} value={r.value}
              open={open === 'o' + r.id} onToggle={() => setOpen(o => (o === 'o' + r.id ? null : 'o' + r.id))}
              actions={(
                <>
                  <button type="button" className="gtc-btn-ghost" disabled={!!busy} onClick={() => remove(r)}>Remove</button>
                  <button type="button" className="gtc-btn-ghost" disabled={!!busy} onClick={() => askAgain(r)}>{busy === 'o' + r.id ? 'Asking…' : 'Ask again'}</button>
                </>
              )} />
          ))}
        </div>
      )}

      {bench.length > 0 && (
        <div className="gtaic-group">
          <div className="gtaic-group-h">From their benches</div>
          {bench.map(b => {
            const id = 'b' + b.member_id + b.key;
            return (
              <Row key={id} title={b.question || b.label} sub={b.bench_name + ' · ' + b.label} value={b.value}
                open={open === id} onToggle={() => setOpen(o => (o === id ? null : id))}
                actions={<button type="button" className="gtc-btn-ghost" disabled={!!busy} onClick={() => askBench(b)}>{busy === id ? 'Asking…' : b.value ? 'Ask again' : 'Ask now'}</button>} />
            );
          })}
        </div>
      )}
    </div>
  );
}
